/**
 * Author: Harsh Agrawal
 * Summary: This page display the option to choose between different payment method.
 */


import React, { useState, useEffect, useRef } from 'react'
import Header from '../../../components/layout/header'
import Footer from '../../../components/layout/footer'
import OrderSummary from '../../../components/orderSummary'
import applepaysvg2 from '../../../assets/img/payment-cards/cn-payment-applepay2.svg'
import visa2 from '../../../assets/img/payment-cards/cn-payment-visa2.svg'
import mastercard2 from '../../../assets/img/payment-cards/cn-payment-mastercard2.svg'
import { useNavigate } from 'react-router-dom';
import localStore from '../../../utils/localstore'
import { useSearchParams } from 'react-router-dom'
import cookieStore from '../../../utils/cookie'
import { CHECKOUT_STATUS, USER_PAYMENT_METHOD_PREFERENCE, resolvePayPalCurrencies } from '../../../utils/constants'
import { salesforcePayload } from '../../../utils/functions'
import { createItemPayload, parseItemsFromURLSearchParams } from '../../../utils/cart'
import { useDispatch, useSelector } from 'react-redux'
import { createCampaignMember } from '../../../reducers/salesforce'
import { setCheckoutPayload, setCart, fetchEstimateForNewSubscription } from '../../../reducers/cart'
import { fetchUserBillingDetails } from '../../../reducers/accountDetails'
import { updateUserPaymentMethodPreference } from '../../../reducers/cart'
import { initChargebee } from '../../../reducers/chargebee'
import { displayError } from '../../../reducers/snackbar'
import { MdArrowForwardIos } from "react-icons/md";
import { ButtonBase } from '@mui/material';

// Keeps the ButtonBase tiles visually identical to the original `container-gray` divs:
// ButtonBase resets display/padding/border-radius/font/align, so re-assert them here so the
// rendered button looks the same while being keyboard-operable.
const paymentOptionSx = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  p: '20px',
  mt: '20px',
  backgroundColor: '#f7f8f9',
  borderRadius: '6px',
  color: 'inherit',
  font: 'inherit',
  overflow: 'hidden',
};


function PaymentMethodOption() {
  const { userValidationResponse, email } = useSelector((state) => state.auth);
  const { items, promoCode, count, estimate } = useSelector((state) => state.cart)
  const { gdprEmailUpdates, billingDetail } = useSelector((state) => state.accountDetails);
  const { isApplePaySupported, paymentModule, isCbInitiated, payPalCurrencies } = useSelector((state) => state.chargebee);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isPayPalEnabled = paymentModule?.includes('paypal_express_checkout')
    && resolvePayPalCurrencies(payPalCurrencies).includes(estimate?.currencyCode);

  useEffect(() => {
    if (searchParams.has("token") || cookieStore.get("vvstore")) {
      let token = searchParams.get("token") ?? cookieStore.get("vvstore");
      localStore.set('token', token);
    }

    // If user landed here directly (e.g. return from login redirect), re-initialize Redux state
    // that would normally have been populated by /checkout's entry-point useEffect. The async
    // setup (Chargebee init + estimate fetch) is wrapped so a failure surfaces through the
    // standard checkout error notification instead of being swallowed.
    const restoreCheckoutState = async () => {
      if (!isCbInitiated) {
        await dispatch(initChargebee());
      }
      if (Object.keys(items ?? {}).length === 0 && searchParams.toString().length > 0) {
        const parsed = parseItemsFromURLSearchParams(searchParams);
        if (parsed?.cart && Object.keys(parsed.cart).length > 0) {
          dispatch(setCart({ count: parsed.count, items: parsed.cart, promoCode: parsed.promoCode }));
          const payload = { items: createItemPayload(parsed.cart, parsed.count) };
          if (parsed.promoCode) payload.promoCode = parsed.promoCode;
          if (email) payload.email = email;
          await dispatch(fetchEstimateForNewSubscription(payload));
        }
      }
    };

    restoreCheckoutState().catch((error) => {
      dispatch(displayError(error));
    });

    if (userValidationResponse?.userStatus === "EXIST") {
      fetchUserDetails(email);
    }
  }, [])


  const createSalesforceCampaign = (firstName, lastName, email, checkoutStatus = CHECKOUT_STATUS.COMPLETED) => {
    let payload = salesforcePayload({ firstName, lastName, email, checkoutStatus });
    if (gdprEmailUpdates !== undefined) {
      payload = { ...payload, 'OptinMarketing': gdprEmailUpdates ? "YES" : "NO" }
    }
    return dispatch(createCampaignMember(payload))
  }

  const redirectUser = (preference) => {
    const isWalletFlow = preference === USER_PAYMENT_METHOD_PREFERENCE.APPLE_PAY
      || preference === USER_PAYMENT_METHOD_PREFERENCE.PAYPAL;
    const skipBillingDetails = isWalletFlow;

    if (isWalletFlow) {
      let payload = createSubscriptionPayload();
      dispatch(updateUserPaymentMethodPreference({ 'preference': preference }));
      createSalesforceCampaign(email, email, email, CHECKOUT_STATUS.INITIATED);
      dispatch(setCheckoutPayload({ payload }));
    } else {
      dispatch(updateUserPaymentMethodPreference({ 'preference': preference }));
    }

    let { userStatus, status, hasActiveSubscription } = userValidationResponse;
    // EXIST users with no active subscription (cancelled/expired) get the same flow as NEW / EXIST_NO_SUB
    const treatAsNewSubscription = userStatus === "NEW"
      || userStatus === "EXIST_NO_SUB"
      || (userStatus === "EXIST" && hasActiveSubscription === false);

    if (status === "success") {
      if (treatAsNewSubscription) {
        navigate(skipBillingDetails ? "/checkout/addPaymentMethod" : '/checkout/addbillingdetails');
      } else if (userStatus === "EXIST") {
        navigate("/checkout/viewbillingdetails");
      }
    }
  }

  const fetchUserDetails = (email) => {
    dispatch(fetchUserBillingDetails(email))
      .then((response) => {

      })
      .catch((error) => {
        dispatch(displayError(error));
      });
  };

  const createSubscriptionPayload = () => {

    const items_ = createItemPayload(items, count);

    const stateCodeSupportedCountry = ["IN", "US", "CA"];

    let billingDetails_ = {};

    billingDetails_ = {
      "firstName": `${billingDetail?.firstName}`,
      "lastName": `${billingDetail?.lastName}`,
      "addressLine1": `${billingDetail?.addressLine1}`,
      "addressLine2": `${billingDetail?.addressLine2}`,
      "city": `${billingDetail?.city}`,
      "zip": `${billingDetail?.zip}`,
      "country": `${billingDetail?.country}`,
      "state": `${billingDetail?.state}`
    };

    let payload = {
      "billingDetail": billingDetails_,
      "email": email,
      "items": items_,
    }

    if (promoCode) {
      payload = { ...payload, promoCode: promoCode };
    }

    return payload;
  }

  return (
    <>
      <div className="body body--alt2">
        <Header />

        <main className="main main--fullh">
          <section className="section personal-details">
            <div className="grid-cont">


              <div className='mt-8'>
                <h3 className="personal-details__heading">Choose how to pay</h3>
                <h5 className='payment-detail__subtitle'>Your payment is safe and secure, change how you pay anytime</h5>
              </div>

              <div>
                {
                  isApplePaySupported &&
                  <ButtonBase className='container-gray cursor-pointer' sx={paymentOptionSx} aria-label="Pay with Apple Pay" onClick={(e) => redirectUser(USER_PAYMENT_METHOD_PREFERENCE.APPLE_PAY)}>
                    <div className='flex justify-between'>
                      <div>
                        <img src={applepaysvg2} width="40" />
                      </div>
                      <div className='font-bold'>
                        <MdArrowForwardIos />
                      </div>
                    </div>

                  </ButtonBase>
                }

                {
                  isPayPalEnabled &&
                  <ButtonBase className='container-gray cursor-pointer' sx={paymentOptionSx} aria-label="Pay with PayPal" onClick={(e) => redirectUser(USER_PAYMENT_METHOD_PREFERENCE.PAYPAL)}>
                    <div className='flex justify-between'>
                      <div className='flex flex-row gap-2'>
                        <div style={{ fontWeight: 'bold', color: '#003087' }}>Pay<span style={{ color: '#009cde' }}>Pal</span></div>
                      </div>
                      <div className='font-bold'>
                        <MdArrowForwardIos />
                      </div>
                    </div>
                  </ButtonBase>
                }

                <ButtonBase className='container-gray cursor-pointer' sx={paymentOptionSx} aria-label="Pay with credit or debit card" onClick={(e) => redirectUser(USER_PAYMENT_METHOD_PREFERENCE.CARD)}>
                  <div className='flex justify-between'>
                    <div className='flex flex-row gap-2'>
                      <div>Credit or Debit Card</div>
                      <div className='card-border'>
                        <img src={visa2} width="25" style={{ 'marginTop': '-1px' }} />
                      </div>
                      <div className='card-border'  >
                        <img src={mastercard2} width="25" style={{ 'marginTop': '2px' }} />
                      </div>
                    </div>

                    <div className='font-bold'>
                      <MdArrowForwardIos />
                    </div>
                  </div>
                </ButtonBase>

              </div>

              <OrderSummary />

            </div>

          </section >
        </main >

        <Footer />

      </div >
    </>
  )
}

export default PaymentMethodOption